import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { NvButton, NvCtaBanner } from "@/components/navigator";
import type { AgentDetail } from "@/services/navigator/agent-detail-service";
import type { InternalDetailData } from "@/services/navigator/internal-detail-types";

import type { HeaderActions } from "./agent-detail-header";
import { DemoVideoModal } from "./internal-demo-modal";
import { InternalAnchorBar, InternalClassificationBar, InternalHeader, type InternalAnchor } from "./internal-header";
import { RestrictedBody } from "./internal-restricted";
import { BeforeAfterSection, DifferentiatorsSection, MetricsSection, OverviewSection, TechSection, TrustSection } from "./internal-sections-a";
import { AdoptionSection, CompositionSection, DemoSection, ImplementationSection, PolicySection, UsecasesSection, WorkflowSection } from "./internal-sections-b";
import { ComparisonSection, ProjectSection } from "./internal-sections-c";

// 내부 Asset 상세 (Figma ver.7 03-09 Idea · 03-10 MVP · 03-11 Pilot · 03-12 Production · 03-09-2 열람 제한 · 03-10-1 조합 구성 · 03-12-01 데모 영상 모달).
// PwC 내부 개발 에이전트(`detail_type = internal`)는 외부 에이전트 상세와 같은 틀(헤더 → 분류 띠 → 섹션 바로가기 → 섹션)에 개발 단계별 구성을 얹어요.
// 서버가 보는 사람과 개발 단계에 맞게 거른 값을 주고(내부 전용 칩 · 섹션은 PwC 내부 회원에게만), 값이 있는 섹션 · 바로가기 탭만 그려요.
// 진입 권한: 비회원 · 승인 대기 · 공급자는 X, 수요자는 대외 공개 + MVP 이상만, PwC 내부 회원은 전체(기밀은 열람 승인 필요) — 서버(agent_visibility)가 정해요.
// 열람 신청 · 소스코드 열람 신청은 2차라 만들지 않아요.

// GNB(--nv-gnb-height 72)가 맨 위에 붙어 있고, 바로가기 바(약 53)는 그 아래에 붙어요. 스크롤 계산에 두 높이를 더해요.
const GNB_HEIGHT = 72;
const ANCHOR_BAR_HEIGHT = 53;

const METRICS_TITLES = { idea: "목표 지표", mvp: "검증 지표", pilot: "검증 지표", production: "성과 지표" } as const;

// 프로젝트 정보는 보여줄 값이 하나라도 있을 때만 (이름 · 담당자 · 개발과 소스코드 · 같은 프로젝트의 다른 Agent)
function hasProject(i: InternalDetailData) {
  const p = i.project;
  return !!p && !!(p.name || p.task_name || p.partner || p.owner || p.development || p.others.length);
}

function anchorsOf(i: InternalDetailData): InternalAnchor[] {
  if (i.restricted) {
    // 본문은 내려오지 않으므로 개발 단계의 표준 구성을 그대로 보여줘요
    const pilot = i.stage === "pilot" || i.stage === "production";
    return [
      { key: "overview", label: "개요" },
      { key: "before-after", label: "도입 전후" },
      { key: "differentiators", label: "차별화 포인트" },
      { key: "tech", label: "핵심 AI 기술" },
      { key: "metrics", label: METRICS_TITLES[i.stage] },
      { key: "trust", label: "보안과 신뢰" },
      { key: "workflow", label: "워크플로우" },
      ...(i.multi_agent ? [{ key: "composition", label: "조합 구성" }] : []),
      ...(i.stage !== "idea" ? [{ key: "demo", label: "시연/데모" }] : []),
      { key: "usecases", label: "유즈케이스" },
      { key: "adoption", label: "도입 조건" },
      ...(pilot ? [{ key: "implementation", label: "구현과 확산" }] : []),
      ...(i.stage === "production" ? [{ key: "policy", label: "운영과 사업 정책", tag: true }] : []),
      { key: "project", label: "프로젝트 정보", tag: true },
      { key: "comparison", label: "유사 에이전트 비교" },
    ];
  }
  const list: InternalAnchor[] = [];
  if (i.overview) list.push({ key: "overview", label: "개요" });
  if (i.before_after) list.push({ key: "before-after", label: "도입 전후" });
  if (i.differentiators?.length) list.push({ key: "differentiators", label: "차별화 포인트" });
  if (i.tech) list.push({ key: "tech", label: "핵심 AI 기술" });
  if (i.metrics) list.push({ key: "metrics", label: i.metrics.title });
  if (i.trust) list.push({ key: "trust", label: "보안과 신뢰" });
  if (i.workflow) list.push({ key: "workflow", label: "워크플로우" });
  if (i.composition) list.push({ key: "composition", label: "조합 구성" });
  if (i.demo) list.push({ key: "demo", label: "시연/데모" });
  if (i.usecases?.length) list.push({ key: "usecases", label: "유즈케이스" });
  if (i.adoption) list.push({ key: "adoption", label: "도입 조건" });
  if (i.implementation?.length) list.push({ key: "implementation", label: "구현과 확산" });
  if (i.policy) list.push({ key: "policy", label: "운영과 사업 정책", tag: true });
  if (hasProject(i)) list.push({ key: "project", label: "프로젝트 정보", tag: true });
  if (i.comparison) list.push({ key: "comparison", label: "유사 에이전트 비교" });
  return list;
}

export function InternalDetail({ detail, actions }: { detail: AgentDetail; actions: HeaderActions }) {
  const internal = detail.internal;
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const anchors = useMemo(() => (internal ? anchorsOf(internal) : []), [internal]);

  // 스크롤 위치에 따라 현재 섹션 강조
  useEffect(() => {
    if (!anchors.length) return;
    const onScroll = () => {
      let current = anchors[0].key;
      for (const a of anchors) {
        const el = document.getElementById(`agent-${a.key}`);
        if (el && el.getBoundingClientRect().top <= GNB_HEIGHT + ANCHOR_BAR_HEIGHT + 120) current = a.key;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [anchors]);

  if (!internal) return null;

  const scrollTo = (key: string) => {
    const el = document.getElementById(`agent-${key}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (GNB_HEIGHT + ANCHOR_BAR_HEIGHT), behavior: "smooth" });
  };
  const videoUrl = internal.restricted ? null : internal.demo_video_url ?? null;

  return (
    <div>
      <InternalHeader detail={detail} actions={actions} onPlay={() => setVideoOpen(true)} />
      <InternalClassificationBar detail={detail} />
      {anchors.length > 0 && <InternalAnchorBar anchors={anchors} active={active ?? anchors[0].key} onGo={scrollTo} />}

      {internal.restricted ? (
        <RestrictedBody />
      ) : (
        <>
          {internal.overview && <OverviewSection data={internal.overview} />}
          {internal.before_after && <BeforeAfterSection data={internal.before_after} />}
          {!!internal.differentiators?.length && <DifferentiatorsSection items={internal.differentiators} />}
          {internal.tech && <TechSection data={internal.tech} />}
          {internal.metrics && <MetricsSection data={internal.metrics} />}
          {internal.trust && <TrustSection data={internal.trust} />}
          {internal.workflow && <WorkflowSection data={internal.workflow} agentName={detail.name} />}
          {internal.composition && <CompositionSection data={internal.composition} />}
          {internal.demo && <DemoSection data={internal.demo} production={internal.stage === "production"} />}
          {!!internal.usecases?.length && <UsecasesSection data={internal.usecases} />}
          {internal.adoption && <AdoptionSection data={internal.adoption} />}
          {!!internal.implementation?.length && <ImplementationSection items={internal.implementation} />}
          {internal.policy && <PolicySection data={internal.policy} />}
          {internal.project && hasProject(internal) && <ProjectSection data={internal.project} />}
          {internal.comparison && <ComparisonSection data={internal.comparison} />}

          <section className="mx-auto max-w-[1440px] px-5 pb-[100px] pt-[100px] xl:px-[152px]">
            <NvCtaBanner
              title="도입 검토를 시작해보세요"
              description="범위 산정, PoC 설계, 공급사 연결을 PwC가 지원합니다."
              actions={
                <>
                  <NvButton asChild>
                    <Link to={actions.inquiryPath} onClick={actions.onInquiry}>
                      PwC와 함께 도입 문의하기
                    </Link>
                  </NvButton>
                  <NvButton variant="secondary" asChild>
                    <Link to={`/agents/${detail.id}/definition`}>상세 정의서 보기</Link>
                  </NvButton>
                </>
              }
            />
          </section>
        </>
      )}

      {videoUrl && <DemoVideoModal open={videoOpen} onOpenChange={setVideoOpen} url={videoUrl} agentName={detail.name} />}
    </div>
  );
}
